import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { RosterTemplate } from '$models'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'
import { rosterService } from '$services'
import dayjs from 'dayjs'
import { RostersSlice } from '../slice'

interface RosterTemplatesState {
  showDeactivated: boolean
}

export interface RosterTemplatesSlice extends RosterTemplatesState {
  /**
   * Resets RosterTemplates Slice state
   */
  resetRosterTemplatesSlice: () => void

  setShowDeactivated: () => void

  /**
   * Create a Roster Template
   * @param successMessage Success Message to display if the creation was successful
   * @param errorMessage Error Message to display if the creation has failed
   * @returns
   */
  createRosterTemplate: (
    successMessage?: string,
    errorMessage?: string
  ) => Promise<RosterTemplate | null>

  /**
   * Deactivate a Roster Template
   * @param rosterTemplateId to deactivate
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  deactivateOrRestoreRosterTemplate: (
    rosterTemplateId: string,
    action: 'deactivate' | 'restore',
    successMessage?: string,
    errorMessage?: string
  ) => Promise<RosterTemplate | null>

  /**
   * Copy a Roster Template
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  copyRosterTemplate: (
    rosterId: string,
    templateName: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<RosterTemplate | null>
}

const initialState: RosterTemplatesState = {
  showDeactivated: false,
}

const createRosterTemplatesSlice: StateCreator<
  RosterTemplatesSlice & RostersSlice & SharedSlice,
  [],
  [],
  RosterTemplatesSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  resetRosterTemplatesSlice: () => set(initialState),

  setShowDeactivated: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),

  // State API calls
  createRosterTemplate: async (
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const newRosterTemplate: RosterTemplate =
        await rosterService.createRosterTemplate()

      if (!newRosterTemplate) {
        logger.error(`${errorMessage} - newRosterTemplate doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      return newRosterTemplate
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return null
    }
  },

  deactivateOrRestoreRosterTemplate: async (
    rosterTemplateId: string,
    action: 'deactivate' | 'restore',
    successMessage?: string,
    errorMessage?: string
  ) => {
    const rosterTemplate = get().rosterTemplates.find(
      (template) => template.id === rosterTemplateId
    )
    if (!rosterTemplate) {
      logger.error(`${errorMessage} - rosterTemplate doesn't exist`)
      enqueueLoadedSnackbar(errorMessage, 'error')
      return null
    }

    try {
      // We do an optimistic update of the rosterTemplates state so it it reflected in the UI
      // and if it fails we revert the state back to the original state
      set((state) => ({
        rosterTemplates: state.rosterTemplates.map((template) => {
          if (template.id === rosterTemplateId) {
            return {
              ...template,
              datestampDeleted: action === 'deactivate' ? dayjs() : null,
            }
          }
          return { ...template }
        }),
      }))

      rosterTemplate.datestampDeleted = action === 'deactivate' ? dayjs() : null
      const updatedRosterTemplate: RosterTemplate =
        await rosterService.updateRosterTemplate(rosterTemplate)

      if (!updatedRosterTemplate) {
        logger.error(`${errorMessage} - updatedRosterTemplate doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      return updatedRosterTemplate
    } catch (error) {
      // Rollback the state
      set((state) => ({
        rosterTemplates: state.rosterTemplates.map((template) => {
          if (template.id === rosterTemplateId) {
            return {
              ...template,
              datestampDeleted:
                action === 'deactivate'
                  ? null
                  : rosterTemplate.datestampDeleted,
            }
          }
          return { ...template }
        }),
      }))
      exceptionMessageHandler(error, errorMessage)
      return null
    }
  },

  copyRosterTemplate: async (
    rosterId: string,
    templateName: string,
    successMessage?: string,
    errorMessage?: string
  ) => {
    set(() => ({ isLoading: true }))
    try {
      const copiedRosterTemplate: RosterTemplate =
        await rosterService.copyRosterTemplate(rosterId, templateName)

      if (!copiedRosterTemplate) {
        logger.error(`${errorMessage} - addedStaffMember doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      return copiedRosterTemplate
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createRosterTemplatesSlice
